import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  InputBase,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import React, { createElement, useEffect, useState } from 'react';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import RenderHTML from '../../components/preview/RenderHTML';
import DateLabel from '../../components/label/DateLabel';
import { TimeLabel } from '../../components/label/TimeLabel';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExoAvatar from '../../components/exo/ExoAvatar';
import { NavLink, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import Pagination from '../../components/special/Pagination';
import { ExoSkeleton } from '../../components/exo/ExoSkeleton';
import DeleteIcon from '@mui/icons-material/Delete';
import { API_DELETE, API_POST, DBRequest, useConfig } from '../../api/api';
import ExoAlert from '../../components/exo/ExoAlert';
import LaunchIcon from '@mui/icons-material/Launch';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import { ECSenderPreview } from '../../components/email-system/ECSenderPreview';
import { RenderHtmlEmail } from '../../components/email-system/ECEmailConversation';
import NoDataAnimation from '../../components/animation/NoDataAnimation';
import ECFromStack from '../../components/email-system/ECFromStack';

export const ProjectInteractionHistory = ({
  interactions,
  setInteractions,
  onEdit,
  onFeedback,
  loadData,
  noKeyAction,
  projectId,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();

  const [reqDel, setReqDel] = useState(null);
  const [currentType, setCurrentType] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  const [originalInteractions, setOriginalInteractions] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  if (!interactions)
    return (
      <Box className="overflow-y-auto max-h-[500px] xl:max-h-[9999px] xl:h-full flex flex-col gap-1">
        <ExoSkeleton size="102px" n={5} />
      </Box>
    );

  function handleDelete(element) {
    setReqDel(null);
    DBRequest({
      config,
      path: `interactions/${element.id}`,
      method: API_DELETE(),
      onResponse: () => {
        const newInteractions = interactions.data.filter(
          (obj) => obj.id !== element.id
        );
        setInteractions({ ...interactions, data: newInteractions });
      },
      onFeedback,
    });
  }

  function handleTypeChange(e, type) {
    setCurrentType(type);
    loadData(type);
  }

  function handleSearch(term) {
    if (term === '' && originalInteractions) {
      setInteractions(originalInteractions);
      setSearchLoading(false);
      return;
    }
    if (!originalInteractions) setOriginalInteractions(interactions);
    setSearchLoading(true);
    DBRequest({
      config,
      path: 'search/interactions',
      method: API_POST({
        q: term,
        type: currentType == 'all' ? null : currentType,
        projectId: projectId,
      }),
      onResponse: (res) => {
        setInteractions(res);
        setSearchLoading(false);
      },
    });
  }

  return (
    <Box className="max-h-[500px] xl:max-h-[9999px] pt-2 xl:h-full overflow-y-auto flex flex-col gap-1 overflow-hidden">
      <ToggleButtonGroup
        className="absolute top-[6px] right-[6px]"
        size="small"
        value={currentType}
        exclusive
        onChange={handleTypeChange}
        aria-label="Interactions"
        sx={{
          '& button': {
            padding: '3px 8px',
          },
        }}
      >
        <ToggleButton value="meeting">
          <InsertInvitationIcon />
        </ToggleButton>
        <ToggleButton value="call">
          <LocalPhoneIcon />
        </ToggleButton>
        <ToggleButton value="note">
          <EditNoteIcon />
        </ToggleButton>
        <ToggleButton value="email">
          <EmailIcon />
        </ToggleButton>
      </ToggleButtonGroup>
      {isLoading ? (
        <ExoSkeleton size="102px" n={25} />
      ) : (
        <>
          <Box
            className="flex items-center overflow-hidden transition-all duration-300"
            sx={{
              width: searchExpanded ? '100%' : '32px',
              minWidth: '32px',
              height: '32px',
              flexShrink: 0,
              bgcolor: searchExpanded ? colors.bgInput : colors.card,
              borderRadius: '8px',
              border: searchExpanded
                ? `1px solid ${colors.info}`
                : '1px solid transparent',
              '&:hover': {
                bgcolor: searchExpanded ? colors.bgInput : colors.selected,
              },
            }}
          >
            {searchExpanded ? (
              <>
                {searchLoading ? (
                  <CircularProgress
                    size={18}
                    sx={{ ml: 1, color: colors.info }}
                  />
                ) : (
                  <SearchIcon
                    sx={{ fontSize: '18px', ml: 1, opacity: 0.5 }}
                  />
                )}
                <InputBase
                  placeholder={t('Search history...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch(searchTerm);
                    if (e.key === 'Escape') {
                      setSearchExpanded(false);
                      setSearchTerm('');
                      setSearchResults(null);
                      setSearchLoading(false);
                      if (originalInteractions) {
                        setInteractions(originalInteractions);
                        setOriginalInteractions(null);
                      }
                    }
                  }}
                  size="small"
                  className="flex-1 px-2"
                  autoFocus
                  sx={{ fontSize: '13px' }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchExpanded(false);
                    setSearchTerm('');
                    setSearchResults(null);
                    setSearchLoading(false);
                    if (originalInteractions) {
                      setInteractions(originalInteractions);
                      setOriginalInteractions(null);
                    }
                  }}
                >
                  <ClearIcon sx={{ fontSize: '16px' }} />
                </IconButton>
              </>
            ) : (
              <IconButton
                size="small"
                onClick={() => setSearchExpanded(true)}
                sx={{ width: '100%', borderRadius: '8px' }}
              >
                <SearchIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            )}
          </Box>

          {interactions?.data?.length ? (
            interactions?.data?.map((element) => (
              <Interaction
                key={element.id}
                interaction={element}
                onEdit={() => onEdit(element)}
                onDelete={() => setReqDel(element)}
              />
            ))
          ) : (
            <Box className="w-full flex items-center justify-center pt-[12px]">
              <NoDataAnimation size="150px" />
            </Box>
          )}
        </>
      )}
      <ExoAlert
        onSubmit={() => handleDelete(reqDel)}
        open={Boolean(reqDel)}
        onClose={() => setReqDel(null)}
        alert={{
          submitLabel: t('Delete'),
          message: t('Are you sure you want to permanently delete this entry?'),
        }}
      />
      {interactions?.meta?.lastPage !== 1 && (
        <Pagination
          data={interactions}
          setData={setInteractions}
          isSmall
          setLoading={setIsLoading}
          postParams={
            searchTerm == ''
              ? null
              : {
                  q: searchTerm,
                  type: currentType == 'all' ? null : currentType,
                  projectId: projectId,
                }
          }
        />
      )}
    </Box>
  );
};

export const Interaction = ({ interaction, onEdit, onDelete }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [openEmail, setOpenEmail] = useState(false);
  const [openParticipants, setOpenParticipants] = useState(false);

  if (!interaction) return;
  const {
    title,
    note,
    dateTime,
    type,
    participants,
    sourceType,
    sourceId,
    source,
  } = interaction;

  const typeMap = {
    call: {
      Icon: LocalPhoneIcon,
      label: t('call'),
      color: colors.success,
    },
    note: {
      Icon: EditNoteIcon,
      label: t('note'),
      color: colors.text,
    },
    meeting: {
      Icon: InsertInvitationIcon,
      label: t('meeting'),
      color: colors.info,
    },
    email: {
      Icon: EmailIcon,
      label: t('email'),
      color: colors.info,
      route: 'emails',
    },
  };
  const typeObj = typeMap[type];

  // Utility function to extract plain text from HTML
  function extractInitialText(htmlString, charLimit = 150) {
    const tempDiv = document.createElement('div');

    // Parse HTML
    tempDiv.innerHTML = htmlString;

    // Remove <style>, <script>, and hidden elements
    const stylesAndScripts = tempDiv.querySelectorAll(
      'style, script, head, link, meta'
    );
    stylesAndScripts.forEach((el) => el.remove());

    // Extract visible text
    let textContent = tempDiv.textContent || tempDiv.innerText || '';
    textContent = textContent.replace(/[\u200B-\u200D\uFEFF]/g, '');
    const cleaned = textContent.trim().replace(/\s+/g, ' ');

    return (
      cleaned.slice(0, charLimit) + (cleaned.length > charLimit ? '...' : '')
    );
  }

  // Get preview text (2 lines approx)
  const previewText = extractInitialText(source?.htmlBody || source?.textBody);

  return (
    <Box
      className="relative"
      sx={{
        bgcolor: colors.card,
        borderRadius: '10px',
        padding: '6px 8px',
        paddingBottom: '20px',
        '& .redirectButton': {
          transition: '0.3s ease',
          opacity: 0,
          transform: 'translate(5px)',
        },
        '& .delButton': {
          transition: '0.3s ease',
          bgcolor: colors.error + '30',
          color: colors.error,
          border: `1px solid ${colors.error}80`,
          opacity: 0,
          transform: 'translate(-5px)',
        },
        '& .editButton': {
          transition: '0.3s ease',
          bgcolor: colors.success + '30',
          color: colors.success,
          border: `1px solid ${colors.success}80`,
          opacity: 0,
          transform: 'translate(-5px)',
        },
        ':hover': {
          '& .editButton, & .delButton, & .redirectButton': {
            opacity: 1,
            transform: 'translate(0px)',
          },
        },
      }}
    >
      <Box className="flex justify-between gap-2">
        <Box className="flex flex-col gap-1 w-full">
          <Box className="flex gap-3 items-start justify-between pb-1">
            <Typography
              fontSize="14px"
              textAlign="left"
              fontWeight={600}
              className="flex gap-2 items-center"
            >
              {typeObj && (
                <Box
                  sx={{
                    bgcolor: typeObj.color + '30',
                    width: '25px',
                    height: '25px',
                  }}
                  className="rounded-md p-1 flex items-center justify-center"
                >
                  {createElement(typeObj.Icon, {
                    sx: { color: typeObj.color, fontSize: '16px' },
                  })}
                </Box>
              )}
              {title}
              {typeObj.route && sourceId && (
                <IconButton
                  fontSize="small"
                  className="redirectButton"
                  onClick={() => navigate(`/${typeObj.route}/${sourceId}`)}
                >
                  <LaunchIcon fontSize="small" />
                </IconButton>
              )}
            </Typography>

            {/* Action buttons */}
            <Box className="flex gap-1 shrink-0">
              {onEdit && (
                <IconButton
                  onClick={onEdit}
                  size="small"
                  className="editButton"
                >
                  <EditIcon sx={{ fontSize: '15px' }} />
                </IconButton>
              )}
              {onDelete && (
                <IconButton
                  onClick={onDelete}
                  size="small"
                  className="delButton"
                >
                  <DeleteIcon sx={{ fontSize: '15px' }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {type === 'email' && (
            <Box className="flex flex-col gap-1">
              <Button
                sx={{ padding: 0, justifyContent: 'space-between' }}
                onClick={() => setOpenEmail(!openEmail)}
                className="flex items-center justify-between"
              >
                <Typography
                  fontWeight={500}
                  fontSize="12px"
                  className="opacity-60 text-left line-clamp-2"
                  sx={{ maxWidth: 'calc(100% - 24px)', overflow: 'hidden' }}
                >
                  {openEmail
                    ? t('Hide Email Content')
                    : previewText || t('read email')}
                </Typography>
                <ArrowDropDownIcon
                  sx={{
                    transform: `rotate(${openEmail ? 180 : 0}deg)`,
                    transition: 'transform 0.2s',
                  }}
                />
              </Button>
              <Collapse in={openEmail}>
                {openEmail && (
                  <>
                    {source?.from && (
                      <ECFromStack
                        from={source?.from}
                        avatarSize="47px"
                        to={source?.to}
                        cc={source?.cc}
                        showEmail
                        openFromMobile={true}
                      />
                    )}
                    <Box className="pt-2 pb-1">
                      <Divider />
                    </Box>
                    <Box
                      sx={{
                        padding: '6px',
                        bgcolor: 'rgba(235, 235, 235, 0.8)',
                      }}
                    >
                      <RenderHtmlEmail
                        htmlBody={source?.htmlBody || source?.textBody}
                        inlineAttachments={source?.inlineAttachments}
                      />
                    </Box>
                  </>
                )}
              </Collapse>
            </Box>
          )}
        </Box>
      </Box>

      <Box className="pl-1">
        <RenderHTML HTML={note} emptyHTML={' '} />
      </Box>

      <Box className="pt-2 pb-1">
        <Divider />
      </Box>

      {participants && (
        <Box className="flex flex-col gap-1 py-1">
          <Button
            sx={{ padding: 0, justifyContent: 'space-between' }}
            onClick={() => setOpenParticipants(!openParticipants)}
            className="flex items-center justify-between"
          >
            <Typography fontWeight={500} fontSize="12px" className="opacity-60">
              {`${t('Participants')} (${participants.length})`}
            </Typography>
            <ArrowDropDownIcon
              sx={{
                transform: `rotate(${openParticipants ? 180 : 0}deg)`,
              }}
            />
          </Button>
          <Collapse in={openParticipants}>
            <Box className="flex flex-col gap-1">
              {participants?.map((element) => (
                <Participant data={element} key={element.participant.id} />
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      <Typography
        fontSize="11px"
        className="opacity-50 absolute bottom-1 right-2"
      >
        <DateLabel date={dateTime} /> -{' '}
        <TimeLabel time={dateTime} format="HH:mm" />
      </Typography>
    </Box>
  );
};

const Participant = ({ data }) => {
  const { participant, type } = data;

  const nameMap = {
    companyLead: participant.nameShort || participant.name,
    personLead: `${participant?.salutation} ${participant?.lastName} ${participant?.firstName}`,
    company: participant.nameShort || participant.name,
    contact: `${participant?.salutation} ${participant?.lastName} ${participant?.firstName}`,
    user: `${participant?.salutation} ${participant?.lastName} ${participant?.firstName}`,
  };

  const routeMap = {
    companyLead: `/leads/companies/${participant.id}`,
    personLead: `/leads/persons/${participant.id}`,
    company: `/companies/${participant.id}`,
    contact: `/contacts/${participant.id}`,
    user: `/employees/${participant.id}`,
  };

  return (
    <NavLink to={routeMap[type]}>
      <Box className=" pl-2 flex flex-row justify-start items-center gap-2 whitespace-nowrap">
        <ExoAvatar name={nameMap[type]} size="35px" />
        {nameMap[type]}
      </Box>
    </NavLink>
  );
};
